import { useForm, Control, UseFormWatch, UseFormSetValue } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  calcClientDerivatives,
  appliedHealthCareInflation,
  carePhaseDefList,
  carePhaseDefs,
  CarePhase,
} from '@shared';
import type { CarePhaseDef } from '@shared';
import {
  selectClient,
  selectMaybeClientPartner,
  previewClientUpdates,
} from '@client/model';

type FormFields = {
  ltcAtAge: number;
  healthcareInflation: number;
} & Record<CarePhaseDef['key'], number>;

export type EditAssumptionsFormFields = FormFields;

const ltcAtAgeValidation = z.coerce.number().int().min(40).max(150);
const careDurationSchema = z.coerce.number().int().min(0);

const baseSchema = {
  healthcareInflation: z.coerce
    .number()
    .min(0, 'Inflation rate must be at least 0%')
    .max(20, 'Inflation rate cannot exceed 20%'),
  ...Object.fromEntries(
    carePhaseDefList.map(carePhaseDef => [
      carePhaseDefs[carePhaseDef.value].key,
      careDurationSchema,
    ]),
  ),
};

export function useEditAssumptionsForm() {
  const client = useSelector(selectClient);
  const clientPartner = useSelector(selectMaybeClientPartner);
  const dispatch = useDispatch();

  const {
    intakeSurvey: { clientHasStartedLtc, clientCurrentCarePhase },
    appliedInferenceSet: { ltcAtAge, ltcDurationYears },
    carePhaseDurationSelections = {},
    phaseCosts = [],
  } = client;

  const currentCarePhaseIndex = carePhaseDefList.findIndex(
    carePhaseDef => carePhaseDef.value === clientCurrentCarePhase,
  );

  const isPastPhase = useCallback(
    (phaseIndex: number) => phaseIndex < (currentCarePhaseIndex === -1 ? 0 : currentCarePhaseIndex),
    [currentCarePhaseIndex],
  );

  const [previewClient, setPreviewClient] = useState<Client>(client);

  const predictedDuration = useCallback(
    (carePhaseDef: CarePhaseDef) => {
      if (!phaseCosts || !phaseCosts[carePhaseDef.index] || !ltcDurationYears) {
        return 0;
      }
      return Math.round(phaseCosts[carePhaseDef.index].phaseDurationRatio * ltcDurationYears * 12);
    },
    [phaseCosts, ltcDurationYears],
  );

  const predictedLtcAtAge = client.inferenceSet?.ltcAtAge ?? 65;

  const schema = z.object(
    clientHasStartedLtc
      ? baseSchema
      : {
          ...baseSchema,
          ltcAtAge: ltcAtAgeValidation,
        },
  );

  const {
    control,
    setValue,
    watch,
    formState: { errors: formErrors },
  } = useForm<FormFields>({
    defaultValues: {
      ltcAtAge: client.clientCustomInferences.ltcAtAge ?? predictedLtcAtAge,
      healthcareInflation: parseFloat((appliedHealthCareInflation(client) * 100).toFixed(1)),
      ...Object.fromEntries(
        carePhaseDefList.map(carePhaseDef => [
          carePhaseDefs[carePhaseDef.value].key,
          carePhaseDurationSelections[carePhaseDef.value] ?? predictedDuration(carePhaseDef),
        ]),
      ),
    },
    resolver: zodResolver(schema),
  });

  const updatePreviewClient = useCallback(
    (updatedClient: Client) => {
      const updatedClientWithDerivatives = calcClientDerivatives(updatedClient, clientPartner);
      setPreviewClient(updatedClientWithDerivatives as Client);
      dispatch(previewClientUpdates(updatedClientWithDerivatives));
    },
    [clientPartner, dispatch],
  );

  const getResetDurationPatches = useCallback(() => {
    const resetCarePhaseDurations: Record<CarePhase, number> = {} as Record<CarePhase, number>;
    carePhaseDefList.forEach(carePhaseDef => {
      resetCarePhaseDurations[carePhaseDef.value] = predictedDuration(carePhaseDef);
      setValue(carePhaseDefs[carePhaseDef.value].key, predictedDuration(carePhaseDef), {
        shouldDirty: true,
      });
    });
    return { carePhaseDurationSelections: resetCarePhaseDurations };
  }, [predictedDuration, setValue]);

  const handleResetDurations = useCallback(() => {
    const patches = getResetDurationPatches();
    updatePreviewClient({ ...previewClient, ...patches });
  }, [getResetDurationPatches, previewClient, updatePreviewClient]);

  const isAllPredictedDurations = useCallback(() => {
    try {
      return carePhaseDefList.every(
        carePhaseDef =>
          watch(carePhaseDefs[carePhaseDef.value].key) === predictedDuration(carePhaseDef),
      );
    } catch {
      return false;
    }
  }, [predictedDuration, watch]);

  return {
    client,
    clientHasStartedLtc,
    control: control as Control<FormFields>,
    watch: watch as UseFormWatch<FormFields>,
    setValue: setValue as UseFormSetValue<FormFields>,
    formErrors,
    previewClient,
    setPreviewClient,
    updatePreviewClient,
    predictedDuration,
    predictedLtcAtAge,
    isPastPhase,
    handleResetDurations,
    getResetDurationPatches,
    isAllPredictedDurations,
  };
}
